import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import {Container, Row, Col} from 'react-bootstrap';
import { TextFieldAtom, ModalAtom, DropdownAtom, MultiSelectAtom } from "../../../../components/atoms/atom";
import { setting } from '../../../../store/services/index';
import { AuthContext } from '../../../../contexts/AuthProvider';
import { showError } from "../../../../utils/method";
import { HELP_MODES } from "../../../../utils/constant";

function AddUserModal(props) {
    const { show, onClose, onSubmit, mode, userInfo = {} } = props;
    const { t } = useTranslation();
    // storing form fields
    const [firstName, setFirstName] = useState(userInfo?.firstName || '');
    const [lastName, setLastName] = useState(userInfo?.lastName || '');
    const [userName, setUserName] = useState(userInfo?.userName || '');
    const [email, setEmail] = useState(userInfo?.email || '');
    const [password, setPassword] = useState('');
    const [contactNumber, setMobileNumber] = useState(userInfo?.contactNumber || '');
    const [operatingMaterial, setOpertaingMaterial] = useState(userInfo?.operatingMaterial || []);
    const [role, setRole] = useState(userInfo?.role?.id || '');
    const [group, setGroup] = useState(userInfo?.group?.id || '');
    const [department, setDepartment] = useState(userInfo?.department?.id || '');
    // storing error obj
    const [errorObj, setErrorObj] = useState({
        firstName: '',
        lastName: '',
        userName: '',
        email: '',
        password: '',
        contactNumber: '',
        operatingMaterial: '',
        role: '',
        group: '',
        department: '',
    });
    // storing dropdown options
    const [roleOptions, setRoleOptions] = useState([]);
    const [groupOptions, setGroupOptions] = useState([]);
    const [operatingMaterialOptions, setOpertaingMaterialOptions] = useState([]);
    const [departmentList, setDepartmentList] = useState([]);
    const [departmentOptions, setDepartmentOptions] = useState([]);
    const { auth, helpMode } = useContext(AuthContext);

    const fetchDropDownOptions = async () => {
        try {
            const roleResult = await setting.getRole();
            setRoleOptions((roleResult.data || []).map((role) => { 
                return {
                    label: role.name,
                    value: role.id,
                }
            }));
            
            const departmentResult = await setting.getDepartment();
            setDepartmentList(departmentResult?.data);
            setDepartmentOptions((departmentResult?.data || []).map((dept) => (
                {
                    label: dept.name,
                    value: dept.id,
                }
            )));
            
            const operatingMaterialResult = await setting.getResource();
            setOpertaingMaterialOptions((operatingMaterialResult.data || []).map((operMatOpt) => { 
                return {
                    label: operMatOpt.name,
                    value: operMatOpt.id,
                }
            }));
        } catch (error) {
            showError(t, error);
        }
    };

    useEffect(() => {
        fetchDropDownOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        populateGroupOptions(userInfo?.department?.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo?.organisation, departmentList]);

    const onChangeDepartment = (e) => {
        const selectedDepartment = e.target.value;
        setDepartment(selectedDepartment);
        setGroup(null);
        populateGroupOptions(selectedDepartment);
    };

    const populateGroupOptions = (selectedDepartment) => {
        const deptObj = departmentList.find((d) => d.id === selectedDepartment);
        if (deptObj) {
            const groupList = deptObj.groups.map((group) => ({
                label: group.name,
                value: group.id,
            }));
            setGroupOptions(groupList);
        }
    };

    const onAddUser = () => {
        let localErrorObj = {};
        if (!firstName) {
            localErrorObj.firstName = 'error-first-name-required';
        }
        if (!lastName) { 
            localErrorObj.lastName = 'error-last-name-required';
        }
        if (!userName) {
            localErrorObj.userName = 'error-user-name-required';
        }
        if (!email) {
            localErrorObj.email = 'error-email-required';
        }
        if (!password && mode === 'add') {
            localErrorObj.password = 'error-password-required';
        }
        if (!role) {
            localErrorObj.role = 'error-reporting-role-required';
        }
        setErrorObj({...localErrorObj});
        const hasError = Object.keys(localErrorObj).length ;
        if (!hasError){
            const getIds = (inputArray) => {
                const resultantId = [];
                inputArray.forEach((option) => resultantId.push(option.value));
                return resultantId;
            }
            onSubmit({
                ...userInfo,
                firstName,
                lastName,
                userName,
                email,
                password,
                contactNumber,
                operatingMaterial: getIds(operatingMaterial),
                role,
                group,
                department
            })
        }       
    };


    return (
        <ModalAtom 
            title={t(mode === 'add' ? 'add-user' : 'edit-user')}
            titleHelpText={helpMode === HELP_MODES.ADVANCE ? t('help-advance.add-user-modal-title') : ''}
            show={show} 
            handleClose={onClose}
            handleSubmit={onAddUser}
            body={ <Container>
                <Row>
                    <Col>
                        <TextFieldAtom 
                            type="text"
                            placeholder={t('first-name')}
                            required
                            value={firstName}
                            error={errorObj.firstName}
                            onChange={(e) => {
                                setFirstName(e.target.value);
                                setErrorObj({ ...errorObj, firstName: e.target.value ? '' : 'error-first-name-required' })
                            }}
                        />
                    </Col>
                    <Col>
                        <TextFieldAtom 
                            type="text"
                            placeholder={t('last-name')}
                            required
                            value={lastName}
                            error={errorObj.lastName}
                            onChange={(e) => {
                                setLastName(e.target.value);
                                setErrorObj({ ...errorObj, lastName: e.target.value ? '' : 'error-last-name-required' })
                            }}                        
                        />
                    </Col>
                  
                </Row>
                <Row className="mt-4 mb-4">
                    <Col>
                        <TextFieldAtom 
                            type="text"
                            placeholder={t('username')}
                            required
                            value={userName}
                            error={errorObj.userName}
                            autoComplete="new-password"
                            onChange={(e) => {
                                setUserName(e.target.value);
                                setErrorObj({ ...errorObj, userName: e.target.value ? '' : 'error-user-name-required' })
                            }} 
                        />
                    </Col>
                    <Col>
                        {['ADMIN', 'SUPER_ADMIN'].includes(auth.role) && <TextFieldAtom 
                            type="password"
                            placeholder={t('password')}
                            required
                            value={password}
                            error={errorObj.password}
                            autoComplete="new-password"
                            onChange={(e) => {
                                setPassword(e.target.value);
                                if (mode === 'add') {
                                    setErrorObj({ ...errorObj, password: e.target.value ? '' : 'error-password-required' })
                                }
                            }} 
                        />}
                    </Col>
                </Row>
                <Row className="mt-4 mb-4">
                    <Col>
                        <TextFieldAtom 
                            type="text"
                            placeholder={t('email-address')}
                            required
                            value={email}
                            error={errorObj.email}
                            autoComplete="new-password"
                            onChange={(e) => {
                                setEmail(e.target.value);
                                setErrorObj({ ...errorObj, email: e.target.value ? '' : 'error-email-required' })
                            }} 
                        />
                    </Col>
                    <Col>
                        <TextFieldAtom 
                            type="text"
                            placeholder={t('mobile-number')}
                            value={contactNumber}
                            error={errorObj.contactNumber}
                            onChange={(e) => {
                                setMobileNumber(e.target.value);
                            }}                         
                        />
                    </Col>
                </Row>
                <Row className="mt-4 mb-4">
                    <Col>
                        <DropdownAtom 
                            placeholder={t('select-department')}
                            value={department}
                            options= {departmentOptions}
                            error={errorObj.department}
                            onChange={onChangeDepartment}                       
                        />
                       
                    </Col>
                    <Col>
                        <DropdownAtom 
                            placeholder={t('select-group')}
                            value={group}
                            options= {groupOptions}
                            error={errorObj.group}
                            onChange={(e) => {
                                setGroup(e.target.value);
                            }}                       
                        />
                       
                    </Col>
                </Row>
                <Row>   
                    <Col>
                        <DropdownAtom 
                            placeholder={t('select-reporting-role')}
                            value={role}
                            required
                            options= {roleOptions}
                            error={errorObj.role}
                            onChange={(e) => {
                                setRole(e.target.value);
                                setErrorObj({ ...errorObj, role: e.target.value ? '' : 'error-reporting-role-required' })
                            }}                       
                        />
                    </Col>
                    <Col>
                        <MultiSelectAtom
                            selectedOption={operatingMaterial}
                            placeholder={t('select-operating-material')}
                            options = {operatingMaterialOptions}
                            error={errorObj.operatingMaterial}
                            onChange={(e) => {
                                setOpertaingMaterial(e);
                            }}/>
                    </Col>
                </Row>
            </Container>}
        />
    );
}

export default AddUserModal;
